import {
  FileItem,
  Files,
} from "@coworker/apprestructured/src/task-management/components/Files/Files";
import { Links } from "@coworker/apprestructured/src/task-management/components/Links/Links";
import { CommentsTabItem } from "@coworker/apprestructured/src/task-management/components/LogsAndComments/CommentsTab/CommentsTab";
import {
  DEFAULT_TAB,
  LogsAndComments,
  Tab,
} from "@coworker/apprestructured/src/task-management/components/LogsAndComments/LogsAndComments";
import styles from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheet.module.css";
import UserAndTeamViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserAndTeamViewerFragment/UserAndTeamViewerFragment";
import ViewerFragment, {
  ViewerFragmentCommonProps,
  ViewerFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import ViewerFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragmentConfig";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { useTaskComments } from "@coworker/apprestructured/src/task-management/services/useTaskComments";
import { useTaskCommentsImages } from "@coworker/apprestructured/src/task-management/services/useTaskCommentsImages";
import { useTaskLogs } from "@coworker/apprestructured/src/task-management/services/useTaskLogs";
import { Comment } from "@coworker/apprestructured/src/task-management/types/Comment";
import { Log } from "@coworker/apprestructured/src/task-management/types/Log";
import { timestampToTimeDiff } from "@coworker/apprestructured/src/task-management/util";
import { useTranslation } from "@coworker/locales";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import { UseToastNotificationsResult } from "../../Notifications/hooks/useToastNotificationsState";

const NO_COMMENT_IMAGE_URLS: string[] = [];

interface TaskViewerSheetContentProps {
  taskId: string;
  headerElement: ReactNode;
  imagesElement: ReactNode;
  viewerFragments: ViewerFragmentConfig[];
  viewerFragmentCommonProps: ViewerFragmentCommonProps;
  setViewerFragmentRef?: (
    key: string
  ) => (element: ViewerFragmentHandle | null) => void;
  startElement: ReactNode;
  fileItems: FileItem[];
  links: string[];
  addCommentText: string;
  addCommentImages: File[];
  isCreatingComment: boolean;
  addCommentImageUploaderIsReady: boolean;
  onAddCommentTextChange: (text: string) => void;
  setAddCommentImages: (images: File[]) => void;
  onAddCommentClick: () => void;
  showToast: UseToastNotificationsResult["showToast"];
}

export const TaskViewerSheetContent = memo(
  (props: TaskViewerSheetContentProps) => {
    const { t } = useTranslation();
    const {
      taskId,
      headerElement,
      viewerFragmentCommonProps,
      viewerFragments,
      imagesElement,
      startElement,
      setViewerFragmentRef,
      fileItems,
      links,
      addCommentText,
      addCommentImages,
      isCreatingComment,
      addCommentImageUploaderIsReady,
      onAddCommentTextChange,
      setAddCommentImages,
      onAddCommentClick,
      showToast,
    } = props;
    // Note that this just reflects the currently active tab, not control it
    const [historyAndCommentsTab, setHistoryAndCommentsTab] =
      useState<Tab>(DEFAULT_TAB);

    const { data: logs, isLoading: isLoadingLogs } = useTaskLogs(taskId);
    const { data: comments, isLoading: isLoadingComments } =
      useTaskComments(taskId);
    const {
      data: commentImages,
      isLoading: isLoadingCommentsImages,
      isError: isCommentImagesError,
    } = useTaskCommentsImages(
      historyAndCommentsTab === "comments" ? taskId : null
    );

    useEffect(() => {
      if (isCommentImagesError) {
        showToast({
          text: t("imageCouldNotBeLoadedString"),
        });
      }
    }, [isCommentImagesError, showToast, t]);

    const logsTabItems = useMemo(
      () =>
        logs?.map((log: Log) => {
          const params = log.params?.map((param) => param) || [];
          const translationParams =
            log.translation_params.map((param) => t(param)) || [];

          return {
            title: log.title,
            content: t(log.translation_key, {
              params: params,
              translationParams: translationParams,
            }),
            time: timestampToTimeDiff({
              dateTime: log.created_at,
              t,
            }),
          };
        }) || [],
      [logs, t]
    );

    const commentsTabItems = useMemo<CommentsTabItem[]>(
      () =>
        comments?.map((comment: Comment) => {
          const commentImageUrls = () => {
            if (comment.image_ids.length === 0) {
              return NO_COMMENT_IMAGE_URLS;
            } else if (commentImages === undefined) {
              return LOADING;
            } else {
              const imageUrls = commentImages
                .filter(({ id }) => comment.image_ids.includes(id))
                .map(({ signed_url }) => signed_url);
              if (imageUrls.length !== comment.image_ids.length) {
                // We have created a new comment and are currently refetching images
                return LOADING;
              } else {
                return imageUrls;
              }
            }
          };

          const commentImageCreateUploads =
            commentImages
              ?.filter(({ id }) => comment.image_ids.includes(id))
              .map(({ name, size, bucket_id }) => ({
                name,
                size: Number(size),
                bucket_id,
              })) ?? [];

          return {
            id: comment.id,
            title: comment.title,
            content: comment.text,
            time: timestampToTimeDiff({
              dateTime: comment.created_at,
              t,
            }),
            imageUrls: commentImageUrls(),
            imageCreateUploads: commentImageCreateUploads,
            creatorUserId: comment.creator_user_id,
          };
        }) || [],
      [comments, t, commentImages]
    );

    return (
      <>
        {headerElement}
        <div className={styles["contentWrapper"]}>
          {imagesElement}
          {viewerFragments.map((config, i) => (
            <div
              className={styles["viewerFragmentSection"]}
              key={`${config.id}-${i}`}
            >
              <ViewerFragment
                config={config}
                commonProps={viewerFragmentCommonProps}
                ref={setViewerFragmentRef?.(`${config.id}-${i}`)}
              />
            </div>
          ))}
          <UserAndTeamViewerFragment
            configProps={{
              title: t("createdByString"),
              userIdField: "creator_user_id",
              teamFromUser: true,
            }}
            commonProps={viewerFragmentCommonProps}
          />
          <UserAndTeamViewerFragment
            configProps={{
              title: t("assignedToTaskFilterString"),
              userIdField: "assigned_user_id",
              teamIdField: "assigned_team_id",
            }}
            commonProps={viewerFragmentCommonProps}
          />
          {startElement}
          <Files items={fileItems} />
          <Links links={links} />
          <LogsAndComments
            taskId={taskId}
            logsTabItems={logsTabItems}
            commentsTabItems={commentsTabItems}
            isLoadingLogs={isLoadingLogs}
            isLoadingComments={isLoadingComments || isLoadingCommentsImages}
            onTabChanged={setHistoryAndCommentsTab}
            addCommentText={addCommentText}
            addCommentImages={addCommentImages}
            disableAllAddCommentInputs={isCreatingComment}
            disableAddCommentButton={!addCommentImageUploaderIsReady}
            onAddCommentTextChange={onAddCommentTextChange}
            onAddCommentImagesChange={setAddCommentImages}
            onAddCommentClick={onAddCommentClick}
          />
        </div>
      </>
    );
  }
);
